<script setup lang="ts">
import { computed } from "vue";
import {
  NModal,
  NTimeline,
  NTimelineItem,
  NTag,
  NButton,
  NDivider,
} from "naive-ui";
import { $t } from "@/locales";

interface Props {
  visible: boolean;
  direction: string;
  fallbackProvider?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
}>();

const show = computed({
  get() {
    return props.visible;
  },
  set(visible: boolean) {
    emit("update:visible", visible);
  },
});

const providerName = computed(() => {
  if (!props.fallbackProvider) return "";
  return (
    $t(`page.login.codeLogin.fallbackProvider.${props.fallbackProvider}`) ||
    props.fallbackProvider
  );
});

const providerLink = computed(() => {
  if (!props.fallbackProvider) return "";
  return (
    $t(`page.login.codeLogin.fallbackProviderLink.${props.fallbackProvider}`) ||
    ""
  );
});

function openProvider() {
  if (providerLink.value) {
    window.open(providerLink.value, "_blank");
  }
}
</script>

<template>
  <NModal
    v-model:show="show"
    preset="card"
    style="width: 90%; max-width: 32rem"
    :title="$t('page.login.codeLogin.fallbackOtpTitle')"
    :bordered="false"
    :style="{ direction: props.direction }"
  >
    <!-- Why SMS failed section -->
    <div class="section">
      <h4 class="section-title">
        {{ $t("page.login.codeLogin.fallbackOtpWhyTitle") }}
      </h4>
      <p class="section-content">
        {{ $t("page.login.codeLogin.fallbackOtpWhyDescription") }}
      </p>
    </div>

    <NDivider />

    <!-- Steps timeline -->
    <div class="section">
      <NTimeline>
        <NTimelineItem type="info">
          <template #icon>
            <NTag size="small" type="primary" round>1</NTag>
          </template>
          <span class="timeline-text">
            {{ $t("page.login.codeLogin.fallbackOtpStep1") }}
          </span>
        </NTimelineItem>
        <NTimelineItem type="info">
          <template #icon>
            <NTag size="small" type="primary" round>2</NTag>
          </template>
          <span class="timeline-text">
            {{
              $t("page.login.codeLogin.fallbackOtpStep2", {
                provider: providerName,
              })
            }}
          </span>
        </NTimelineItem>
        <NTimelineItem type="success">
          <template #icon>
            <NTag size="small" type="success" round>3</NTag>
          </template>
          <span class="timeline-text">
            {{ $t("page.login.codeLogin.fallbackOtpStep3") }}
          </span>
        </NTimelineItem>
      </NTimeline>
    </div>

    <NDivider />

    <!-- Alternative option -->
    <div class="section">
      <h4 class="section-title">
        {{ $t("page.login.codeLogin.fallbackOtpAlternativeTitle") }}
      </h4>
      <p class="section-content alternative-text">
        {{ $t("page.login.codeLogin.fallbackOtpRetryLater") }}
      </p>
    </div>

    <!-- Open provider button -->
    <template #footer>
      <div class="footer-actions">
        <NButton v-if="providerLink" type="primary" @click="openProvider">
          {{
            $t("page.login.codeLogin.openFallbackProvider", {
              provider: providerName,
            })
          }}
        </NButton>
        <NButton @click="show = false">
          {{ $t("common.close") }}
        </NButton>
      </div>
    </template>
  </NModal>
</template>

<style scoped>
.section {
  margin-bottom: 8px;
}

.section-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
  color: var(--n-title-text-color);
}

.section-content {
  font-size: 14px;
  color: var(--n-text-color);
  line-height: 1.6;
}

.timeline-text {
  font-size: 14px;
  line-height: 1.5;
}

.alternative-text {
  padding: 12px;
  background-color: var(--n-color-target);
  border-radius: 6px;
  border: 1px solid var(--n-border-color);
}

.footer-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}
</style>
